/**
 * Fyncall Widget Shim - Tier 2 (15KB gzipped)
 *
 * This shim:
 * 1. Defines the <fyncall-widget> Web Component
 * 2. Uses closed Shadow DOM for complete isolation
 * 3. Renders the launcher button immediately
 * 4. Lazy-loads the full widget app in an iframe on first open
 * 5. Handles cross-frame communication via postMessage
 */
(function(w, d) {
  'use strict';

  // Default configuration
  var DEFAULT_CONFIG = {
    position: 'bottom-right',
    primaryColor: '#7c3aed',
    launcherSize: 'medium', // small=48px, medium=56px, large=64px
    windowWidth: 340,
    windowHeight: 650,
    zIndex: 2147483647,
    showBranding: true,
    showNotificationBadge: true
  };

  // Helper to parse widget dimensions from config (handles "340px" or 340)
  function parseWidgetDimension(value, defaultValue) {
    if (!value) return defaultValue;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      var parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  }

  // SVG Icons - includes all launcher icon options
  var ICONS = {
    // Launcher icons (must match frontend options)
    chat: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>',
    chatDots: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-9 9H7V9h4v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/></svg>',
    support: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/></svg>',
    wave: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7.03 4.95L3.49 8.49c-3.32 3.32-3.32 8.7 0 12.02s8.7 3.32 12.02 0l6.01-6.01c.97-.97.97-2.56 0-3.54-.12-.12-.25-.23-.39-.32l.39-.39c.97-.97.97-2.56 0-3.54-.16-.16-.35-.3-.54-.41.4-.92.23-2.02-.52-2.77-.87-.87-2.22-.96-3.2-.28-.1-.15-.21-.29-.34-.42-.97-.97-2.56-.97-3.54 0l-.39.39c-.09-.14-.2-.27-.32-.39-.97-.97-2.56-.97-3.54 0zM9.17 6.36c.2-.2.51-.2.71 0l4.23 4.24-1.41 1.41-3.52-3.52-.01-.01V6.36zm2.83 2.83l.71-.71 4.24 4.24-.71.71-4.24-4.24z"/></svg>',
    smile: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-6c.78 2.34 2.72 4 5 4s4.22-1.66 5-4H7zm1-3c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zm8 0c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/></svg>',
    send: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>',
    // UI icons
    close: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>',
    minimize: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13H5v-2h14v2z"/></svg>'
  };

  /**
   * Fyncall Widget Web Component
   */
  class FyncallWidget extends HTMLElement {
    // Private fields (closed shadow DOM)
    #shadow;
    #config;
    #urls;
    #iframe;
    #launcher;
    #container;
    #isOpen = false;
    #isLoaded = false;
    #pendingMessages = [];
    #eventListeners = {};
    #unreadCount = 0;
    #launcherVisible = true;
    #proactiveMessage = null;
    #proactiveTimeout = null;
    #proactiveShown = false;
    #proactiveTimers = [];
    #proactiveState = {
      triggersShown: {},
      triggerCount: 0,
      lastTriggerTime: 0,
      scrollDepth: 0,
      scrollListenerBound: false,
      exitIntentBound: false,
      pendingTriggers: [] // Queue triggers that fire while widget is open
    };

    constructor() {
      super();
      // CLOSED mode - shadow root cannot be accessed externally
      // This provides complete isolation and security
      this.#shadow = this.attachShadow({ mode: 'closed' });
    }

    connectedCallback() {
      this.#render();
      this.#bindEvents();
      this.#applyConfig();
    }

    disconnectedCallback() {
      this.#cleanup();
    }

    #render() {
      var config = this.#config || DEFAULT_CONFIG;
      var position = this.#getPositionStyles(config.position || DEFAULT_CONFIG.position);

      // Get widget dimensions from appearance settings (server config) or fall back to defaults
      var appearance = config.appearance || {};
      var widgetWidth = parseWidgetDimension(appearance.widget_width || config.windowWidth, DEFAULT_CONFIG.windowWidth);
      var widgetHeight = parseWidgetDimension(appearance.widget_height || config.windowHeight, DEFAULT_CONFIG.windowHeight);

      console.log('[FyncallShim] Widget dimensions:', widgetWidth + 'x' + widgetHeight, 'from config:', appearance.widget_width, appearance.widget_height);

      this.#shadow.innerHTML = `
        <style>
          :host {
            all: initial;
            position: fixed;
            ${position.launcher}
            z-index: ${config.zIndex || DEFAULT_CONFIG.zIndex};
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          * {
            box-sizing: border-box;
          }

          .fyncall-launcher {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: var(--fyncall-primary, ${config.primaryColor || DEFAULT_CONFIG.primaryColor});
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
            outline: none;
            position: relative;
          }

          .fyncall-launcher:hover {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
          }

          .fyncall-launcher:focus-visible {
            box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .fyncall-launcher:active {
            transform: scale(0.95);
          }

          .fyncall-launcher.hidden {
            opacity: 0;
            pointer-events: none;
            transform: scale(0.8);
          }

          .fyncall-launcher svg {
            width: 28px;
            height: 28px;
            color: white;
            transition: transform 0.2s ease;
          }

          .fyncall-launcher.open svg {
            transform: rotate(90deg);
          }

          .fyncall-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            min-width: 20px;
            height: 20px;
            padding: 0 6px;
            background: #ef4444;
            color: white;
            font-size: 12px;
            font-weight: 600;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transform: scale(0.5);
            transition: opacity 0.2s ease, transform 0.2s ease;
          }

          .fyncall-badge.visible {
            opacity: 1;
            transform: scale(1);
          }
          .fyncall-badge.visible::after {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: inherit;
            animation: ping 1s cubic-bezier(0, 0, .2, 1) infinite;
            z-index: -1;
          }

          .fyncall-frame-container {
            display: none;
            position: fixed;
            ${position.window}
            width: ${widgetWidth}px;
            height: ${widgetHeight}px;
            max-height: calc(100vh - 120px);
            max-width: calc(100vw - 40px);
            z-index: ${(config.zIndex || DEFAULT_CONFIG.zIndex) - 1};
            border-radius: ${appearance.border_radius || '16px'};
            overflow: hidden;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
            background: white;
            opacity: 0;
            transform: translateY(20px) scale(0.95);
            transition: opacity 0.25s ease, transform 0.25s ease;
          }

          .fyncall-frame-container.open {
            display: block;
          }

          .fyncall-frame-container.visible {
            opacity: 1;
            transform: translateY(0) scale(1);
          }

          .fyncall-frame {
            width: 100%;
            height: 100%;
            border: none;
            background: white;
          }

          .fyncall-loading {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: white;
            gap: 16px;
          }

          .fyncall-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e5e7eb;
            border-top-color: var(--fyncall-primary, ${config.primaryColor || DEFAULT_CONFIG.primaryColor});
            border-radius: 50%;
            animation: fyncall-spin 0.8s linear infinite;
          }

          .fyncall-loading-text {
            color: #6b7280;
            font-size: 14px;
          }

          @keyframes fyncall-spin {
            to { transform: rotate(360deg); }
          }

          /* Launcher animations */
          .fyncall-launcher.animate-pulse {
            animation: fyncall-pulse 2s ease-in-out infinite;
          }

          .fyncall-launcher.animate-bounce {
            animation: fyncall-bounce-launcher 1s ease infinite;
          }

          .fyncall-launcher.animate-shake {
            animation: fyncall-shake 0.5s ease infinite;
          }

          @keyframes fyncall-pulse {
            0%, 100% { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); }
            50% { box-shadow: 0 4px 20px var(--fyncall-primary, #7c3aed), 0 4px 12px rgba(0, 0, 0, 0.15); }
          }

          @keyframes fyncall-bounce-launcher {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }

          @keyframes fyncall-shake {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-5deg); }
            75% { transform: rotate(5deg); }
          }
          @keyframes ping {
            75%, 100% {
              opacity: 0;
              transform: scale(2);
            }
          }

          /* Launcher styles */
          .fyncall-launcher.style-rounded {
            border-radius: 16px;
          }

          .fyncall-launcher.style-pill {
            border-radius: 30px;
            width: auto;
            padding: 0 20px;
          }

          /* Launcher sizes - matches frontend: Small=48px, Medium=56px, Large=64px */
          .fyncall-launcher.size-small {
            width: 48px;
            height: 48px;
          }

          .fyncall-launcher.size-small svg {
            width: 22px;
            height: 22px;
          }

          .fyncall-launcher.size-medium {
            width: 56px;
            height: 56px;
          }

          .fyncall-launcher.size-medium svg {
            width: 26px;
            height: 26px;
          }

          .fyncall-launcher.size-large {
            width: 64px;
            height: 64px;
          }

          .fyncall-launcher.size-large svg {
            width: 30px;
            height: 30px;
          }

          /* Proactive message bubble - positioned dynamically based on launcher position */
          .fyncall-proactive {
            position: absolute;
            background: white;
            border-radius: 12px;
            padding: 12px 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            max-width: 280px;
            font-size: 14px;
            color: #1f2937;
            opacity: 0;
            transform: translateY(10px) scale(0.95);
            transition: opacity 0.3s ease, transform 0.3s ease;
            pointer-events: none;
            /* Default position: bottom-right launcher */
            bottom: 70px;
            right: 0;
          }

          /* Position variants for different launcher positions */
          .fyncall-proactive.position-bottom-right {
            bottom: 70px;
            right: 0;
            left: auto;
            top: auto;
          }

          .fyncall-proactive.position-bottom-left {
            bottom: 70px;
            left: 0;
            right: auto;
            top: auto;
          }

          .fyncall-proactive.position-top-right {
            top: 70px;
            right: 0;
            left: auto;
            bottom: auto;
          }

          .fyncall-proactive.position-top-left {
            top: 70px;
            left: 0;
            right: auto;
            bottom: auto;
          }

          .fyncall-proactive.visible {
            opacity: 1;
            transform: translateY(0) scale(1);
            pointer-events: auto;
          }

          /* Top positions animate down instead of up */
          .fyncall-proactive.position-top-right,
          .fyncall-proactive.position-top-left {
            transform: translateY(-10px) scale(0.95);
          }

          .fyncall-proactive.position-top-right.visible,
          .fyncall-proactive.position-top-left.visible {
            transform: translateY(0) scale(1);
          }

          .fyncall-proactive-close {
            position: absolute;
            top: -8px;
            width: 24px;
            height: 24px;
            background: #f3f4f6;
            border: none;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #6b7280;
            transition: background 0.2s;
            /* Default position for bottom-right and top-right */
            right: -8px;
            left: auto;
          }
          .fyncall-proactive-content {
            min-width: 18ch;
            width: min(24ch, 90vw);
            text-wrap: pretty;
          }

          /* Close button on left side for left-positioned launchers */
          .fyncall-proactive.position-bottom-left .fyncall-proactive-close,
          .fyncall-proactive.position-top-left .fyncall-proactive-close {
            left: -8px;
            right: auto;
          }

          .fyncall-proactive-close:hover {
            background: #e5e7eb;
          }

          .fyncall-proactive-arrow {
            position: absolute;
            width: 16px;
            height: 16px;
            background: white;
            transform: rotate(45deg);
            box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
            /* Default position for bottom-right */
            bottom: -8px;
            right: 24px;
            left: auto;
            top: auto;
          }

          /* Arrow positions for different launcher positions */
          .fyncall-proactive.position-bottom-right .fyncall-proactive-arrow {
            bottom: -8px;
            right: 24px;
            left: auto;
            top: auto;
          }

          .fyncall-proactive.position-bottom-left .fyncall-proactive-arrow {
            bottom: -8px;
            left: 24px;
            right: auto;
            top: auto;
          }

          .fyncall-proactive.position-top-right .fyncall-proactive-arrow {
            top: -8px;
            right: 24px;
            left: auto;
            bottom: auto;
            box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.1);
          }

          .fyncall-proactive.position-top-left .fyncall-proactive-arrow {
            top: -8px;
            left: 24px;
            right: auto;
            bottom: auto;
            box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.1);
          }

          /* Mobile responsive styles */
          @media (max-width: 480px) {
            .fyncall-frame-container {
              width: 100%;
              height: 100%;
              max-height: 100%;
              max-width: 100%;
              bottom: 0 !important;
              right: 0 !important;
              left: 0 !important;
              top: 0 !important;
              border-radius: 0;
            }

            .fyncall-launcher {
              bottom: 16px !important;
              right: 16px !important;
            }
          }

          /* Reduced motion support */
          @media (prefers-reduced-motion: reduce) {
            .fyncall-launcher,
            .fyncall-frame-container,
            .fyncall-badge,
            .fyncall-launcher svg {
              transition: none;
            }

            .fyncall-spinner {
              animation: none;
            }
          }
        </style>

        <button class="fyncall-launcher" aria-label="Open chat" aria-expanded="false" aria-haspopup="dialog">
          <span class="fyncall-badge" aria-live="polite" aria-atomic="true">0</span>
          ${ICONS.chat}
        </button>

        <div class="fyncall-proactive" role="status" aria-live="polite">
          <button class="fyncall-proactive-close" aria-label="Close">×</button>
          <div class="fyncall-proactive-content"></div>
          <div class="fyncall-proactive-arrow"></div>
        </div>

        <div class="fyncall-frame-container" role="dialog" aria-label="Chat widget" aria-modal="true">
          <div class="fyncall-loading">
            <div class="fyncall-spinner"></div>
            <div class="fyncall-loading-text">Loading...</div>
          </div>
          <iframe class="fyncall-frame"
                  src="about:blank"
                  title="Fyncall Chat"
                  allow="microphone; camera; clipboard-write; geolocation"
                  loading="lazy">
          </iframe>
        </div>
      `;

      this.#launcher = this.#shadow.querySelector('.fyncall-launcher');
      this.#container = this.#shadow.querySelector('.fyncall-frame-container');
      this.#iframe = this.#shadow.querySelector('.fyncall-frame');
      this.#proactiveMessage = this.#shadow.querySelector('.fyncall-proactive');
    }

    #getPositionStyles(position) {
      var positions = {
        'bottom-right': {
          launcher: 'bottom: 20px; right: 20px;',
          window: 'bottom: 90px; right: 20px;'
        },
        'bottom-left': {
          launcher: 'bottom: 20px; left: 20px;',
          window: 'bottom: 90px; left: 20px;'
        },
        'top-right': {
          launcher: 'top: 20px; right: 20px;',
          window: 'top: 90px; right: 20px;'
        },
        'top-left': {
          launcher: 'top: 20px; left: 20px;',
          window: 'top: 90px; left: 20px;'
        }
      };
      return positions[position] || positions['bottom-right'];
    }

    #bindEvents() {
      // Launcher click
      this.#launcher.addEventListener('click', () => this.toggle());

      // Keyboard support
      this.#launcher.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });

      // Escape to close
      d.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.#isOpen) {
          this.close();
        }
      });

      // Listen for messages from iframe
      w.addEventListener('message', this.#handleMessage.bind(this));

      // Handle iframe load
      this.#iframe.addEventListener('load', () => {
        if (this.#iframe.src !== 'about:blank') {
          this.#onIframeLoaded();
        }
      });

      // Proactive message close button
      var proactiveClose = this.#shadow.querySelector('.fyncall-proactive-close');
      if (proactiveClose) {
        proactiveClose.addEventListener('click', (e) => {
          e.stopPropagation();
          this.#hideProactiveMessage();
        });
      }

      // Click proactive to open chat
      if (this.#proactiveMessage) {
        this.#proactiveMessage.addEventListener('click', () => {
          this.#hideProactiveMessage();
          this.open();
        });
      }
    }

    #handleMessage(event) {
      // Verify origin for security
      if (this.#iframe.contentWindow && event.source !== this.#iframe.contentWindow) {
        return;
      }

      var data = event.data;
      if (!data || typeof data.type !== 'string' || !data.type.startsWith('fyncall:')) {
        return;
      }

      switch (data.type) {
        case 'fyncall:ready':
          this.#onWidgetReady();
          break;
        case 'fyncall:close':
          this.close();
          break;
        case 'fyncall:resize':
          this.#onResize(data);
          break;
        case 'fyncall:unread':
          this.#updateUnreadBadge(data.count);
          break;
        case 'fyncall:event':
          this.#emitEvent(data.event, data.data);
          break;
        case 'fyncall:navigate':
          if (data.url) w.open(data.url, '_blank');
          break;
      }
    }

    #onWidgetReady() {
      this.#isLoaded = true;

      // Hide loading indicator
      var loading = this.#shadow.querySelector('.fyncall-loading');
      if (loading) loading.style.display = 'none';

      // Send any pending messages
      this.#pendingMessages.forEach(msg => this.#postToIframe(msg));
      this.#pendingMessages = [];

      // Send initial config
      this.#postToIframe({
        type: 'fyncall:config',
        config: this.#config,
        urls: this.#urls
      });

      this.#emitEvent('ready');
    }

    #onIframeLoaded() {
      // iframe has loaded, waiting for fyncall:ready message
    }

    #onResize(data) {
      if (typeof data.height === 'number' && data.height > 0) {
        // Get configured height from appearance settings
        var appearance = this.#config?.appearance || {};
        var configHeight = parseWidgetDimension(appearance.widget_height || this.#config?.windowHeight, DEFAULT_CONFIG.windowHeight);
        var maxHeight = Math.min(data.height, configHeight);
        this.#container.style.height = maxHeight + 'px';
      }
    }

    #updateUnreadBadge(count) {
      this.#unreadCount = count || 0;
      var badge = this.#shadow.querySelector('.fyncall-badge');
      if (badge) {
        badge.textContent = this.#unreadCount > 99 ? '99+' : this.#unreadCount;
        // Only show badge if showNotificationBadge is enabled (default true)
        var showBadge = this.#config?._showNotificationBadge !== false;
        badge.classList.toggle('visible', this.#unreadCount > 0 && !this.#isOpen && showBadge);
      }
    }

    #applyConfig() {
      if (!this.#config || !this.#launcher) return;

      var config = this.#config;
      var appearance = config.appearance || {};

      // Extract launcher settings from appearance (snake_case from backend)
      // Fallback to top-level camelCase for backwards compatibility
      var launcherIcon = appearance.launcher_icon || config.launcherIcon || 'chat';
      var launcherStyle = appearance.launcher_style || config.launcherStyle || 'circle';
      var launcherSize = appearance.launcher_size || config.launcherSize || 'medium';
      var launcherAnimation = appearance.launcher_animation || config.launcherAnimation || 'none';
      var showNotificationBadge = appearance.show_notification_badge !== false && config.showNotificationBadge !== false;
      var position = config.position || DEFAULT_CONFIG.position;

      // Store normalized settings for later use (e.g., in close())
      this.#config._launcherIcon = launcherIcon;
      this.#config._launcherStyle = launcherStyle;
      this.#config._launcherSize = launcherSize;
      this.#config._showNotificationBadge = showNotificationBadge;
      this.#config._position = position;

      // Apply position class to proactive message for correct bubble positioning
      if (this.#proactiveMessage) {
        // Remove any existing position classes
        this.#proactiveMessage.classList.remove(
          'position-bottom-right', 'position-bottom-left',
          'position-top-right', 'position-top-left'
        );
        // Add the correct position class
        this.#proactiveMessage.classList.add('position-' + position);
      }

      console.log('[FyncallShim] Applying launcher settings:', {
        launcherIcon: launcherIcon,
        launcherStyle: launcherStyle,
        launcherSize: launcherSize,
        showNotificationBadge: showNotificationBadge
      });

      // Apply primary color
      var primaryColor = config.primaryColor || appearance.primary_color;
      if (primaryColor) {
        this.style.setProperty('--fyncall-primary', primaryColor);
      }

      // Apply launcher icon from config (lookup by name)
      if (launcherIcon && ICONS[launcherIcon]) {
        this.#launcher.innerHTML = `<span class="fyncall-badge">${this.#unreadCount}</span>${ICONS[launcherIcon]}`;
      }

      // Handle auto-open
      if (config.autoOpen) {
        setTimeout(() => this.open(), config.autoOpenDelay || 0);
      }

      // Handle initial visibility
      if (config.launcherHidden || appearance.launcher_hidden) {
        this.hideLauncher();
      }

      // Apply launcher animation
      if (launcherAnimation && launcherAnimation !== 'none') {
        this.#launcher.classList.add('animate-' + launcherAnimation);
      }

      // Apply launcher style (circle is default, so only add class for non-circle)
      if (launcherStyle && launcherStyle !== 'circle') {
        this.#launcher.classList.add('style-' + launcherStyle);
      }

      // Apply launcher size (small=48px, medium=56px, large=64px)
      if (launcherSize === 'small') {
        this.#launcher.classList.add('size-small');
      } else if (launcherSize === 'medium') {
        this.#launcher.classList.add('size-medium');
      } else if (launcherSize === 'large') {
        this.#launcher.classList.add('size-large');
      }

      // Setup proactive message - check both legacy and new formats
      var proactiveConfig = config.proactive_settings || config.proactive;
      if (proactiveConfig && proactiveConfig.enabled && proactiveConfig.triggers && proactiveConfig.triggers.length > 0) {
        // New trigger-based proactive system
        console.log('[FyncallShim] Setting up proactive triggers:', proactiveConfig);
        this.#setupProactiveTriggers(proactiveConfig);
      } else if (config.proactiveEnabled && config.proactiveMessage) {
        // Legacy simple proactive message
        this.#setupProactiveMessage(config);
      }
    }

    #setupProactiveMessage(config) {
      var self = this;
      var delay = config.proactiveDelay || 30000;

      // Don't show if already shown in this session
      try {
        if (sessionStorage.getItem('fyncall_proactive_shown')) {
          return;
        }
      } catch (e) { /* ignore */ }

      this.#proactiveTimeout = setTimeout(function() {
        if (!self.#isOpen && !self.#proactiveShown) {
          self.#showProactiveMessage(config.proactiveMessage);
        }
      }, delay);
    }

    #showProactiveMessage(message) {
      if (!this.#proactiveMessage || this.#proactiveShown) return;

      var content = this.#proactiveMessage.querySelector('.fyncall-proactive-content');
      if (content) {
        content.textContent = message;
      }

      this.#proactiveMessage.classList.add('visible');
      this.#proactiveShown = true;

      // Mark as shown in session
      try {
        sessionStorage.setItem('fyncall_proactive_shown', 'true');
      } catch (e) { /* ignore */ }

      this.#emitEvent('proactive:shown', { message: message });
    }

    #hideProactiveMessage() {
      if (this.#proactiveMessage) {
        this.#proactiveMessage.classList.remove('visible');
      }
      if (this.#proactiveTimeout) {
        clearTimeout(this.#proactiveTimeout);
        this.#proactiveTimeout = null;
      }
    }

    #setupProactiveTriggers(proactiveConfig) {
      var self = this;
      var triggers = proactiveConfig.triggers || [];

      // Log saved state before clearing so we can inspect what was stored
      try {
        var rawState = sessionStorage.getItem('fyncall_proactive_state');
        console.log('[FyncallShim] Raw proactive state before reset:', rawState);
      } catch (e) {
        console.warn('[FyncallShim] Unable to read proactive state:', e);
      }

      // Clear saved state so each load reevaluates triggers
      try {
        sessionStorage.removeItem('fyncall_proactive_state');
      } catch (e) { /* ignore */ }

      triggers.forEach(function(trigger) {
        if (!trigger.enabled) return;
        self.#setupTrigger(trigger, proactiveConfig);
      });
    }

    #setupTrigger(trigger, proactiveConfig) {
      var self = this;
      var type = trigger.type;

      switch (type) {
        case 'page_time':
          var seconds = (trigger.condition && trigger.condition.seconds) || 30;
          var delay = (seconds * 1000) + (trigger.delay || 0);
          console.log('[FyncallShim] Setting up page_time trigger for ' + seconds + 's');
          var timerId = setTimeout(function() {
            self.#evaluateAndShowTrigger(trigger, proactiveConfig);
          }, delay);
          this.#proactiveTimers.push(timerId);
          break;

        case 'scroll_depth':
          if (!this.#proactiveState.scrollListenerBound) {
            this.#proactiveState.scrollListenerBound = true;
            var targetPercent = (trigger.condition && trigger.condition.percentage) || 50;
            console.log('[FyncallShim] Setting up scroll_depth trigger for ' + targetPercent + '%');

            var scrollHandler = this.#throttle(function() {
              var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              var docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
              var scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
              self.#proactiveState.scrollDepth = Math.max(self.#proactiveState.scrollDepth, scrollPercent);

              if (self.#proactiveState.scrollDepth >= targetPercent) {
                self.#evaluateAndShowTrigger(trigger, proactiveConfig);
              }
            }, 200);

            window.addEventListener('scroll', scrollHandler, { passive: true });
          }
          break;

        case 'exit_intent':
          if (!this.#proactiveState.exitIntentBound) {
            this.#proactiveState.exitIntentBound = true;
            console.log('[FyncallShim] Setting up exit_intent trigger');

            document.addEventListener('mouseleave', function(e) {
              if (e.clientY <= 0) {
                self.#evaluateAndShowTrigger(trigger, proactiveConfig);
              }
            });
          }
          break;

        case 'page_visit':
          var urlPattern = trigger.condition && trigger.condition.url_pattern;
          if (urlPattern && this.#matchesUrlPattern(urlPattern)) {
            console.log('[FyncallShim] Page matches url_pattern, setting up trigger');
            var pageTimerId = setTimeout(function() {
              self.#evaluateAndShowTrigger(trigger, proactiveConfig);
            }, trigger.delay || 0);
            this.#proactiveTimers.push(pageTimerId);
          }
          break;

        case 'returning_visitor':
        case 'return_visitor':
          var minVisits = (trigger.condition && trigger.condition.visit_count) || 2;
          var visitCount = this.#getVisitCount();
          console.log('[FyncallShim] Visit count: ' + visitCount + ', required: ' + minVisits);
          if (visitCount >= minVisits) {
            var returnTimerId = setTimeout(function() {
              self.#evaluateAndShowTrigger(trigger, proactiveConfig);
            }, trigger.delay || 2000);
            this.#proactiveTimers.push(returnTimerId);
          }
          break;
      }
    }

    #evaluateAndShowTrigger(trigger, proactiveConfig) {
      var triggerId = trigger.id || (trigger.type + '_' + Date.now());

      // Check if already shown
      if (this.#proactiveState.triggersShown[triggerId]) {
        console.log('[FyncallShim] Trigger ' + triggerId + ' already shown');
        return false;
      }

      // Check if widget is open - queue trigger for later
      if (this.#isOpen) {
        // Queue the trigger to retry when widget closes
        var alreadyQueued = this.#proactiveState.pendingTriggers.some(function(t) {
          return t.trigger.id === trigger.id || (t.trigger.type === trigger.type && !trigger.id);
        });
        if (!alreadyQueued) {
          this.#proactiveState.pendingTriggers.push({ trigger: trigger, proactiveConfig: proactiveConfig });
          console.log('[FyncallShim] Widget is open, queued trigger ' + triggerId + ' for later');
        } else {
          console.log('[FyncallShim] Widget is open, trigger already queued');
        }
        return false;
      }

      // Check max triggers per session
      var maxTriggers = proactiveConfig.max_triggers_per_session || 3;
      if (this.#proactiveState.triggerCount >= maxTriggers) {
        console.log('[FyncallShim] Max triggers reached (' + maxTriggers + ')');
        return false;
      }

      // Check cooldown between triggers
      var cooldown = (proactiveConfig.cooldown_between_triggers || 60) * 1000;
      var timeSinceLast = Date.now() - this.#proactiveState.lastTriggerTime;
      if (this.#proactiveState.lastTriggerTime > 0 && timeSinceLast < cooldown) {
        console.log('[FyncallShim] Cooldown active, ' + Math.round((cooldown - timeSinceLast) / 1000) + 's remaining');
        return false;
      }

      // Show the message
      var message = trigger.message || 'Hi there! Need help finding something?';
      this.#showProactiveMessage(message);

      // Update state
      this.#proactiveState.triggersShown[triggerId] = true;
      this.#proactiveState.triggerCount++;
      this.#proactiveState.lastTriggerTime = Date.now();
      this.#saveProactiveState();

      console.log('[FyncallShim] Trigger ' + triggerId + ' (' + trigger.type + ') shown');
      return true;
    }

    #saveProactiveState() {
      try {
        sessionStorage.setItem('fyncall_proactive_state', JSON.stringify({
          triggersShown: this.#proactiveState.triggersShown,
          triggerCount: this.#proactiveState.triggerCount,
          lastTriggerTime: this.#proactiveState.lastTriggerTime
        }));
      } catch (e) { /* ignore */ }
    }

    #processPendingTriggers() {
      // Process queued triggers after widget closes
      if (this.#proactiveState.pendingTriggers.length === 0) return;

      var self = this;
      // Take the first pending trigger and try to show it after a short delay
      var pending = this.#proactiveState.pendingTriggers.shift();
      if (pending) {
        setTimeout(function() {
          // Only process if widget is still closed
          if (!self.#isOpen) {
            console.log('[FyncallShim] Processing pending trigger: ' + (pending.trigger.id || pending.trigger.type));
            self.#evaluateAndShowTrigger(pending.trigger, pending.proactiveConfig);
          } else {
            // Re-queue if widget opened again
            self.#proactiveState.pendingTriggers.unshift(pending);
          }
        }, 1000); // 1 second delay after close before showing
      }
    }

    #throttle(func, wait) {
      var timeout = null;
      var lastArgs = null;
      return function() {
        var args = arguments;
        if (!timeout) {
          func.apply(this, args);
          timeout = setTimeout(function() {
            timeout = null;
            if (lastArgs) {
              func.apply(this, lastArgs);
              lastArgs = null;
            }
          }, wait);
        } else {
          lastArgs = args;
        }
      };
    }

    #matchesUrlPattern(pattern) {
      if (!pattern) return false;
      try {
        var regexPattern = pattern
          .replace(/[.+^${}()|[\]\\]/g, '\\$&')
          .replace(/\*/g, '.*');
        var regex = new RegExp(regexPattern, 'i');
        return regex.test(window.location.href);
      } catch (e) {
        return false;
      }
    }

    #getVisitCount() {
      try {
        var key = 'fyncall_visit_count';
        var count = parseInt(localStorage.getItem(key) || '0', 10);
        // Increment visit count on first call
        if (!this._visitCounted) {
          count++;
          localStorage.setItem(key, count.toString());
          this._visitCounted = true;
        }
        return count;
      } catch (e) {
        return 1;
      }
    }

    #loadWidgetApp() {
      console.log('[FyncallShim] loadWidgetApp called');

      if (this.#isLoaded) {
        console.log('[FyncallShim] Already loaded, skipping');
        return;
      }

      var config = this.#config || {};
      var urls = this.#urls || {};
      var version = config.widget_version || 'latest';
      var cdnUrl = urls.cdnUrl || 'https://cdn.fyncall.com';
      var baseUrl = urls.baseUrl || 'https://api.fyncall.com';
      var token = urls.token || '';
      var widgetId = urls.widgetId || config.widget_id || '';

      console.log('[FyncallShim] CDN URL:', cdnUrl);
      console.log('[FyncallShim] Base URL:', baseUrl);

      // Check if we need to bypass iframe due to mixed content or local development
      // Direct mode is used when:
      // 1. HTTPS page loading HTTP iframe (mixed content blocked by browsers)
      // 2. Local development (localhost/127.0.0.1) - avoid same-origin issues
      // 3. Explicitly configured via config.forceDirectMode
      var isPageHttps = window.location.protocol === 'https:';
      var isCdnHttp = cdnUrl.startsWith('http://');
      var isLocalDev = cdnUrl.includes('localhost') || cdnUrl.includes('127.0.0.1') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      var forceDirectMode = config.forceDirectMode === true;
      var useDirect = forceDirectMode || isLocalDev || (isPageHttps && isCdnHttp);

      console.log('[FyncallShim] Page HTTPS:', isPageHttps, 'CDN HTTP:', isCdnHttp, 'Local dev:', isLocalDev, 'Use direct:', useDirect);

      if (useDirect) {
        // Direct loading mode - load app.js directly to avoid mixed content iframe block
        console.log('[FyncallShim] Using direct loading (bypassing iframe for local dev)');
        this.#loadAppDirect(cdnUrl, version, token, widgetId, baseUrl, config);
      } else {
        // Iframe mode - use embed.html for full isolation
        console.log('[FyncallShim] Using iframe loading');
        this.#loadAppIframe(cdnUrl, version, token, widgetId, baseUrl);
      }
    }

    #loadAppIframe(cdnUrl, version, token, widgetId, baseUrl) {
      if (this.#iframe.src !== 'about:blank') return;

      var params = new URLSearchParams({
        token: token,
        widget_id: widgetId,
        v: version,
        api: baseUrl
      });

      var embedUrl = cdnUrl + '/' + version + '/embed.html?' + params.toString();
      console.log('[FyncallShim] Loading embed from:', embedUrl);

      this.#iframe.src = embedUrl;
    }

    #loadAppDirect(cdnUrl, version, token, widgetId, baseUrl, config) {
      var self = this;

      // Hide iframe and loading indicator
      this.#iframe.style.display = 'none';
      var loading = this.#shadow.querySelector('.fyncall-loading');
      if (loading) loading.style.display = 'none';

      // Get widget dimensions from appearance settings (server config)
      var appearance = config.appearance || {};
      var widgetWidth = parseWidgetDimension(appearance.widget_width || config.windowWidth, DEFAULT_CONFIG.windowWidth);
      var widgetHeight = parseWidgetDimension(appearance.widget_height || config.windowHeight, DEFAULT_CONFIG.windowHeight);

      // Get border radius from config (default to 16px if not specified)
      var borderRadius = appearance.border_radius || '16px';
      console.log('[FyncallShim] Direct mode dimensions:', widgetWidth + 'x' + widgetHeight, 'from config:', appearance.widget_width, appearance.widget_height);

      // Create fyncall-root container in the main document for app.js to render into
      // Position it to match the shim's widget window
      var appRoot = document.getElementById('fyncall-root');
      var rootStyles = 'position:fixed;bottom:90px;right:20px;width:' + widgetWidth + 'px;height:' + widgetHeight + 'px;max-height:calc(100vh - 120px);max-width:calc(100vw - 40px);z-index:2147483646;border-radius:' + borderRadius + ';overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.2);background:#fff;';
      if (!appRoot) {
        appRoot = document.createElement('div');
        appRoot.id = 'fyncall-root';
        document.body.appendChild(appRoot);
      }
      // Always update styles to reflect current config
      appRoot.style.cssText = rootStyles;

      // Store reference for cleanup
      this._directAppRoot = appRoot;

      // Set up FyncallApp global for the app to use
      // This mirrors what embed.html provides
      window.FyncallApp = window.FyncallApp || {
        token: token,
        widgetId: widgetId,
        apiBaseUrl: baseUrl,
        version: version,
        config: config,
        isReady: false,
        eventListeners: {},

        // Event system (required by app.js)
        on: function(event, callback) {
          if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
          }
          this.eventListeners[event].push(callback);
        },

        emit: function(event, data) {
          var listeners = this.eventListeners[event] || [];
          listeners.forEach(function(cb) {
            try { cb(data); } catch (e) { console.error('[FyncallApp] Event error:', e); }
          });
        },

        // Communication methods (same as embed.html provides)
        notifyParent: function(message) {
          // In direct mode, we handle messages directly
          self.#handleAppMessage(message);
        },
        requestClose: function() {
          self.close();
        },
        setUnreadCount: function(count) {
          self.#updateUnreadBadge(count);
        },
        setReady: function() {
          this.isReady = true;
          console.log('[FyncallShim] App ready (direct mode)');
          self.#onDirectAppReady();
        }
      };


      // Load app.js directly
      var script = document.createElement('script');
      script.src = cdnUrl + '/' + version + '/app.js';
      script.async = true;

      script.onload = function() {
        console.log('[FyncallShim] app.js loaded (direct mode)');
      };

      script.onerror = function() {
        console.error('[FyncallShim] Failed to load app.js');
        self.#showError('Failed to load chat application');
      };

      document.head.appendChild(script);
    }

    #onDirectAppReady() {
      this.#isLoaded = true;

      // In direct mode, the app renders into #fyncall-root which we created
      // Hide the shim's container (we use the direct root instead)
      this.#container.style.display = 'none';

      // Show the direct app root
      if (this._directAppRoot) {
        this._directAppRoot.style.display = 'block';
      }

      this.#emitEvent('ready');
    }

    #handleAppMessage(message) {
      if (!message || !message.type) return;

      switch (message.type) {
        case 'fyncall:ready':
          this.#onDirectAppReady();
          break;
        case 'fyncall:close':
          this.close();
          break;
        case 'fyncall:unread':
          this.#updateUnreadBadge(message.count);
          break;
      }
    }

    #showError(message) {
      var loading = this.#shadow.querySelector('.fyncall-loading');
      if (loading) {
        loading.innerHTML = '<div style="color: #ef4444; text-align: center;">' + message + '</div>';
      }
    }

    #postToIframe(message) {
      if (!this.#isLoaded) {
        this.#pendingMessages.push(message);
        return;
      }

      if (this.#iframe.contentWindow) {
        this.#iframe.contentWindow.postMessage(message, '*');
      }
    }

    #emitEvent(eventName, data) {
      var listeners = this.#eventListeners[eventName];
      if (listeners) {
        listeners.forEach(function(callback) {
          try {
            callback(data);
          } catch (e) {
            console.error('[Fyncall] Event listener error:', e);
          }
        });
      }
    }

    #cleanup() {
      w.removeEventListener('message', this.#handleMessage);
      if (this.#proactiveTimeout) {
        clearTimeout(this.#proactiveTimeout);
      }
      // Clear all proactive trigger timers
      this.#proactiveTimers.forEach(function(timerId) {
        clearTimeout(timerId);
      });
      this.#proactiveTimers = [];
    }

    // Public API methods

    open() {
      if (this.#isOpen) return;
      this.#isOpen = true;

      // Hide proactive message if shown
      this.#hideProactiveMessage();

      // Load widget app on first open (lazy loading)
      this.#loadWidgetApp();

      // Update UI
      this.#container.classList.add('open');
      this.#launcher.classList.add('open');
      this.#launcher.setAttribute('aria-expanded', 'true');
      this.#launcher.innerHTML = `<span class="fyncall-badge">0</span>${ICONS.close}`;

      // Animate in
      requestAnimationFrame(() => {
        this.#container.classList.add('visible');
      });

      // Show direct app root if using direct mode
      if (this._directAppRoot) {
        this._directAppRoot.style.display = 'block';
      }

      // Clear unread badge
      this.#updateUnreadBadge(0);

      // Notify iframe or app
      this.#postToIframe({ type: 'fyncall:opened' });
      if (window.FyncallApp) {
        // Set flag so app.js knows widget is open even if it initializes later
        window.FyncallApp.isWidgetOpen = true;
        if (window.FyncallApp.emit) {
          window.FyncallApp.emit('opened');
        }
      }

      this.#emitEvent('open');
    }

    close() {
      if (!this.#isOpen) return;
      this.#isOpen = false;

      // Animate out
      this.#container.classList.remove('visible');

      // Hide direct app root if using direct mode
      if (this._directAppRoot) {
        this._directAppRoot.style.display = 'none';
      }

      // Update UI after animation
      setTimeout(() => {
        this.#container.classList.remove('open');
      }, 250);

      this.#launcher.classList.remove('open');
      this.#launcher.setAttribute('aria-expanded', 'false');
      // Use configured launcher icon (from normalized settings) or default to chat
      var iconName = this.#config?._launcherIcon || 'chat';
      var launcherIcon = ICONS[iconName] || ICONS.chat;
      var showBadge = this.#config?._showNotificationBadge !== false;
      var badgeVisible = this.#unreadCount > 0 && showBadge ? 'visible' : '';
      this.#launcher.innerHTML = `<span class="fyncall-badge ${badgeVisible}">${this.#unreadCount}</span>${launcherIcon}`;

      // Notify iframe or app
      this.#postToIframe({ type: 'fyncall:closed' });
      if (window.FyncallApp) {
        // Clear flag so app.js knows widget is closed
        window.FyncallApp.isWidgetOpen = false;
        if (window.FyncallApp.emit) {
          window.FyncallApp.emit('closed');
        }
      }

      this.#emitEvent('close');

      // Process any pending proactive triggers that were queued while widget was open
      this.#processPendingTriggers();
    }

    toggle() {
      this.#isOpen ? this.close() : this.open();
    }

    identify(userData) {
      this.#postToIframe({ type: 'fyncall:identify', data: userData });
      return this;
    }

    track(event, properties) {
      this.#postToIframe({ type: 'fyncall:track', event: event, properties: properties });
      return this;
    }

    on(eventName, callback) {
      if (!this.#eventListeners[eventName]) {
        this.#eventListeners[eventName] = [];
      }
      this.#eventListeners[eventName].push(callback);
      return this;
    }

    off(eventName, callback) {
      var listeners = this.#eventListeners[eventName];
      if (listeners) {
        var index = listeners.indexOf(callback);
        if (index > -1) listeners.splice(index, 1);
      }
      return this;
    }

    setLocale(locale) {
      this.#postToIframe({ type: 'fyncall:setLocale', locale: locale });
      return this;
    }

    showLauncher() {
      this.#launcherVisible = true;
      this.#launcher.classList.remove('hidden');
      return this;
    }

    hideLauncher() {
      this.#launcherVisible = false;
      this.#launcher.classList.add('hidden');
      return this;
    }

    setConfig(config) {
      this.#config = config;
      // Only apply config if already rendered (launcher exists)
      // Otherwise, connectedCallback will call #applyConfig after #render
      if (this.#launcher) {
        this.#applyConfig();
      }
      return this;
    }

    setUrls(urls) {
      this.#urls = urls;
      return this;
    }

    isOpen() {
      return this.#isOpen;
    }

    destroy() {
      this.#cleanup();
      this.remove();
    }

  }

  // Register custom element
  if (!customElements.get('fyncall-widget')) {
    customElements.define('fyncall-widget', FyncallWidget);
  }

  // Shim interface for loader
  w.FyncallShim = {
    element: null,

    processQueue: function(queue, config, urls) {
      // Create and mount widget element
      this.element = d.createElement('fyncall-widget');
      this.element.setConfig(config || {});
      this.element.setUrls(urls || {});
      d.body.appendChild(this.element);

      // Map Fyncall namespace to element methods
      var F = w.Fyncall;
      F.open = function() { return FyncallShim.element.open(); };
      F.close = function() { return FyncallShim.element.close(); };
      F.toggle = function() { return FyncallShim.element.toggle(); };
      F.identify = function(data) { return FyncallShim.element.identify(data); };
      F.track = function(event, props) { return FyncallShim.element.track(event, props); };
      F.on = function(event, cb) { return FyncallShim.element.on(event, cb); };
      F.off = function(event, cb) { return FyncallShim.element.off(event, cb); };
      F.setLocale = function(locale) { return FyncallShim.element.setLocale(locale); };
      F.showLauncher = function() { return FyncallShim.element.showLauncher(); };
      F.hideLauncher = function() { return FyncallShim.element.hideLauncher(); };
      F.isOpen = function() { return FyncallShim.element.isOpen(); };
      F.destroy = function() { return FyncallShim.element.destroy(); };
      F._element = this.element;

      // Clear queue reference
      F._q = null;

      // Process queued commands
      queue.forEach(function(item) {
        var method = item[0];
        var args = item[1];
        if (typeof F[method] === 'function') {
          F[method].apply(F, args);
        }
      });

      return this.element;
    }
  };

})(window, document);
